import React, { useEffect, useState } from 'react';
import {
  useNavigate } from 'react-router-dom';
import { Container,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Button } from '@mui/material';
import { getAuth } from 'firebase/auth';
import { useTheme } from '@mui/material/styles';
import { Helmet } from 'react-helmet';
import { ArrowBack as ArrowBackIcon,
  Warning as AlertTriangleIcon,
} from '@mui/icons-material';
import { fetchSupplements } from '../Utils';
import config from '../config';
import SupplementsTable from './SupplementsTable';
import { DietStackService } from '../services/dietStackService';
import ProFeature from './common/ProFeature';
import { checkStackStaleness, getCurrentUserProfile, deriveAgeFromDOB, normalizeSexFromGender } from '../utils/dietStackUtils';
import { NourishStackIcon } from '../icons';
import { r } from '../theme/tokens';

function DietSupportView() {
  const [stack, setStack] = useState(null);
  const [supplementMap, setSupplementMap] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [isStale, setIsStale] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const theme = useTheme();
  const navigate = useNavigate();
  const auth = getAuth();

  useEffect(() => {
    let isMounted = true;

    async function fetchStackAndSupplements() {
      setLoading(true);
      setError('');
      
      const user = auth.currentUser;
      if (!user) {
        setError('You must be logged in to view your diet support stack.');
        setLoading(false);
        return;
      }

      try {
        // Fetch supplements and diet support stack info in parallel
        const [supplementsList, stackInfo] = await Promise.all([
          fetchSupplements(config),
          DietStackService.getDietStackInfo()
        ]);

        // Process supplements
        const newSupplementMap = {};
        if (Array.isArray(supplementsList)) {
          supplementsList.forEach(sup => {
            newSupplementMap[sup.key] = sup.name;
          });
        } else {
          console.error("DietSupportView: Fetched supplements data is not an array:", supplementsList);
        }
        if (isMounted) {
          setSupplementMap(newSupplementMap);
        }

        // Process system stack
        if (stackInfo.stack) {
          if (isMounted) {
            setStack(stackInfo.stack);

            // Check if stack is stale
            const staleness = await checkStackStaleness(stackInfo.stack);
            setIsStale(staleness);
          }
        } else {
          setError('No diet support stack found. Set your diet preferences in your profile to generate recommendations.');
        }

      } catch (err) {
        console.error('Error fetching diet support stack:', err);
        setError('Failed to load your diet support stack. Please try again.');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    fetchStackAndSupplements();

    return () => {
      isMounted = false;
    };
  }, [auth.currentUser]);

  const handleMainLinkClick = (supplementId, e) => {
    e.preventDefault();
    navigate(`/supplements/${supplementId}`);
  };

  const handleRegenerateStack = async () => {
    setRegenerating(true);
    try {
      // Get current user profile for regeneration
      const userProfile = await getCurrentUserProfile(auth.currentUser.uid);
      
      if (!userProfile) {
        setError('Failed to get user profile. Please try again.');
        setRegenerating(false);
        return;
      }

      const success = await DietStackService.generateStack(userProfile);
      if (success) {
        // Refresh the page to show updated stack
        window.location.reload();
      } else {
        setError('Failed to regenerate stack. Please try again.');
      }
    } catch (err) {
      console.error('Error regenerating stack:', err);
      setError('Failed to regenerate stack. Please try again.');
    } finally {
      setRegenerating(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Helmet>
          <title>Nourish Stack - My Stack</title>
        </Helmet>
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/dashboard')}
            sx={{ mb: 2 }}
          >
            Back to Dashboard
          </Button>
        </Box>
        <Alert severity="info" sx={{ mt: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <ProFeature 
      feature="diet_support"
      upgradeTitle="Nourish Stacks"
      upgradeDescription="Get personalized supplement recommendations based on your dietary preferences and lifestage."
    >
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Helmet>
          <title>{stack?.name || 'Nourish Stack'} - My Stack</title>
          <meta name="description" content="Your personalized diet support supplement recommendations" />
        </Helmet>

        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/dashboard')}
            sx={{ mb: 2 }}
          >
            Back to Dashboard
          </Button>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <NourishStackIcon sx={{ color: theme.palette.primary.main, fontSize: '1.75rem' }} />
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
              {stack?.name || 'Nourish Stack'}
            </Typography>
          </Box>

          <Alert severity="info" sx={{ mb: 3 }}>
            This stack was automatically generated based on your dietary preferences and lifestage to help support your nutritional needs. 
            The recommendations are designed to address potential nutrient gaps in your chosen diet.
          </Alert>
        </Box>

        <Box sx={{ position: 'relative' }}>
          {stack && stack.supplements && stack.supplements.length > 0 ? (
            <SupplementsTable
              supplements={stack.supplements}
              supplementMap={supplementMap}
              readOnly={true}
              handleMainLinkClick={handleMainLinkClick}
            />
          ) : (
            <Alert severity="warning">
              No supplement recommendations found in this stack.
            </Alert>
          )}

          <Box sx={{ mt: 4, p: 3, backgroundColor: theme.palette.grey[50], borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>
              About This Stack
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              This diet support stack is automatically updated whenever you modify your dietary preferences or lifestage information in your profile.
              The recommendations are based on established nutritional science and Recommended Daily Allowances (RDAs) from the National Institutes of Health.
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              <strong>Important:</strong> These are general recommendations based on your diet and demographics. Always consult with your healthcare provider or
              registered dietitian before starting any new supplements, especially if you have medical conditions or dietary restrictions.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Last updated: {stack?.updated_at ? new Date(stack.updated_at.seconds * 1000).toLocaleDateString() : 'Unknown'}
            </Typography>
          </Box>

          {/* Stale Stack Overlay */}
          {isStale && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bgcolor: 'rgba(255, 255, 255, 0.9)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
                backdropFilter: `blur(${r(2)})`
              }}
            >
              <Box
                sx={{
                  textAlign: 'center',
                  p: 4,
                  bgcolor: 'white',
                  borderRadius: 2,
                  boxShadow: 3,
                  maxWidth: 400,
                  mx: 2
                }}
              >
                <AlertTriangleIcon
                  sx={{
                    fontSize: 48,
                    color: 'warning.main',
                    mb: 2
                  }}
                />
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Stack May Be Outdated
                </Typography>
                <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
                  Your profile has been updated since this stack was generated.
                  The recommendations may not reflect your current needs.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                  <Button
                    variant="outlined"
                    onClick={() => setIsStale(false)}
                    sx={{ minWidth: 100 }}
                  >
                    Dismiss
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleRegenerateStack}
                    disabled={regenerating}
                    sx={{ minWidth: 100 }}
                  >
                    {regenerating ? <CircularProgress size={20} /> : 'Update Stack'}
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Container>
    </ProFeature>
  );
}

export default DietSupportView;